import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import AdminLogin from './components/AdminLogin';
import AppShell from './components/AppShell';
import AttendancePage from './components/AttendancePage';
import CanhBaoPage from './components/CanhBaoPage';
import DesktopBlockWarning from './components/DesktopBlockWarning';
import LoginPage from './components/LoginPage';
import { ToastProvider } from './components/ToastProvider';
import { useDevToolsGuard } from './hooks/useDevToolsGuard';
import { getEmployees } from './services/attendanceApi';
import { isMobileDevice } from './utils/deviceDetection';
import './App.css';
import './components/UIState.css';

const AdminPage = lazy(() => import('./components/AdminPage'));
const HistoryPage = lazy(() => import('./components/HistoryPage'));

function AppContent({ employee, setEmployee, isAdminLoggedIn, setIsAdminLoggedIn }) {
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    setIsDesktop(!isMobileDevice());
  }, [employee]);

  const handleEmployeeLogin = (emp) => {
    setEmployee(emp);
    localStorage.setItem('chamcong_employee', JSON.stringify(emp));
  };

  const handleEmployeeLogout = useCallback(() => {
    setEmployee(null);
    localStorage.removeItem('chamcong_employee');
    navigate('/');
  }, [navigate, setEmployee]);

  const forceLogoutToWarning = useCallback(() => {
    setEmployee(null);
    localStorage.removeItem('chamcong_employee');
    navigate('/canh-bao', { replace: true });
  }, [navigate, setEmployee]);

  useDevToolsGuard({
    enabled: Boolean(employee && isDesktop),
    onDetected: forceLogoutToWarning,
  });

  const renderEmployeeShell = ({ content, onBackHome = null, showHistory = true }) => (
    <AppShell
      employee={employee}
      onHistory={showHistory ? () => navigate('/history') : null}
      onBackHome={onBackHome}
      onLogout={handleEmployeeLogout}
    >
      {content}
    </AppShell>
  );

  const shouldBlockEmployee = employee && isDesktop;

  return (
    <div className="app">
      <Suspense fallback={<div className="app-loading">Dang tai...</div>}>
        <Routes>
          <Route
            path="/"
            element={
              employee ? (
                shouldBlockEmployee ? (
                  renderEmployeeShell({
                    content: <DesktopBlockWarning onLogout={handleEmployeeLogout} showLogout={true} />,
                    showHistory: false,
                  })
                ) : (
                  renderEmployeeShell({
                    content: <AttendancePage employee={employee} apiBaseUrl={process.env.REACT_APP_API_URL || ''} />,
                  })
                )
              ) : (
                <LoginPage onLogin={handleEmployeeLogin} onAdminLogin={() => navigate('/admin-login')} />
              )
            }
          />

          <Route
            path="/admin-login"
            element={
              isAdminLoggedIn ? (
                <Navigate to="/admin" replace />
              ) : (
                <AdminLogin onLogin={() => setIsAdminLoggedIn(true)} onBack={() => navigate('/')} />
              )
            }
          />

          <Route
            path="/admin"
            element={isAdminLoggedIn ? <AdminPage /> : <Navigate to="/admin-login" replace />}
          />

          <Route path="/canh-bao" element={<CanhBaoPage />} />

          <Route
            path="/history"
            element={
              employee ? (
                shouldBlockEmployee ? (
                  renderEmployeeShell({
                    content: <DesktopBlockWarning onLogout={handleEmployeeLogout} showLogout={true} />,
                    showHistory: false,
                  })
                ) : (
                  renderEmployeeShell({
                    content: <HistoryPage employee={employee} />,
                    onBackHome: () => navigate('/'),
                    showHistory: false,
                  })
                )
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}

function App() {
  const [employee, setEmployee] = useState(null);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);

  useEffect(() => {
    const savedEmployee = localStorage.getItem('chamcong_employee');
    if (!savedEmployee) return;
    try {
      setEmployee(JSON.parse(savedEmployee));
    } catch {
      localStorage.removeItem('chamcong_employee');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const refreshEmployeeFromServer = async () => {
      if (!employee) return;
      try {
        const employees = await getEmployees(true);
        const matched = employees.find((emp) => String(emp.id) === String(employee.id));
        if (!matched || cancelled) return;

        const changed =
          String(matched.name ?? '') !== String(employee.name ?? '') ||
          String(matched.code ?? '') !== String(employee.code ?? '') ||
          String(matched.email ?? '') !== String(employee.email ?? '') ||
          String(matched.position ?? '') !== String(employee.position ?? '') ||
          String(matched.department ?? '') !== String(employee.department ?? '') ||
          String(matched.project ?? '') !== String(employee.project ?? '') ||
          String(matched.status ?? '') !== String(employee.status ?? '') ||
          String(matched.is_sales ?? '') !== String(employee.is_sales ?? '');

        if (!changed) return;
        setEmployee(matched);
        localStorage.setItem('chamcong_employee', JSON.stringify(matched));
      } catch {
        // best-effort only
      }
    };

    refreshEmployeeFromServer();
    return () => {
      cancelled = true;
    };
  }, [employee]);

  return (
    <ToastProvider>
      <Router>
        <AppContent
          employee={employee}
          setEmployee={setEmployee}
          isAdminLoggedIn={isAdminLoggedIn}
          setIsAdminLoggedIn={setIsAdminLoggedIn}
        />
      </Router>
    </ToastProvider>
  );
}

export default App;
